// Create a random password
export function getRandomPassword(settings) {
  // Determine length
  let length = settings.passwordLength;

  // Create characters map
  const characters = getCharacterMap(settings);

  let canContain = characters;

  if (settings.avoidSimilarCharacters) {
    canContain = canContain
      .split("")
      .filter((char) => !settings.similarCharacters.includes(char))
      .join("");
  }

  // Start with letter
  let firstChar = "";

  if (settings.startWithLetter) {
    let letterPool = "";

    if (settings.enableLowercaseCharacters) {
      letterPool += settings.lowercaseCharacters;
    }

    if (settings.enableUppercaseCharacters) {
      letterPool += settings.uppercaseCharacters;
    }

    if (settings.avoidSimilarCharacters) {
      letterPool = letterPool
        .split("")
        .filter((char) => !settings.similarCharacters.includes(char))
        .join("");
    }

    // Fallback safety (should rarely happen)
    if (letterPool.length > 0) {
      firstChar = getRandomChar(letterPool);
      length -= 1; // remaining length
    }
  }

  // Create must contain list
  const mustContain = [];
  const activeOptions = getActiveSettingsAmount(settings);
  const containRatio = activeOptions / (activeOptions + 2) / activeOptions;
  const containAmount = Math.min(
    4,
    Math.max(1, Math.floor(containRatio * length))
  );

  const characterMaps = {
    uppercaseCharacters: settings.uppercaseCharacters,
    lowercaseCharacters: settings.lowercaseCharacters,
    numberCharacters: settings.numberCharacters,
    specialCharacters: settings.specialCharacters,
  };

  if (settings.avoidSimilarCharacters) {
    Object.keys(characterMaps).forEach((key) => {
      characterMaps[key] = characterMaps[key]
        .split("")
        .filter((char) => !settings.similarCharacters.includes(char))
        .join("");
    });
  }

  [
    ["enableUppercaseCharacters", "uppercaseCharacters"],
    ["enableLowercaseCharacters", "lowercaseCharacters"],
    ["enableNumberCharacters", "numberCharacters"],
    ["enableSpecialCharacters", "specialCharacters"],
  ].forEach(function (item) {
    const enabledKey = item[0];
    const characterMapKey = item[1];
    if (settings[enabledKey] && characterMaps[characterMapKey].length > 0) {
      for (let i = 1; i <= containAmount; i++) {
        const char = getRandomChar(characterMaps[characterMapKey]);
        mustContain.push(char);
      }
    }
  });

  // Fill must contain list
  while (mustContain.length < length && canContain.length > 0) {
    const char = getRandomChar(canContain);
    mustContain.push(char);
  }

  // Create password
  let password = "";
  while (mustContain.length) {
    const index = Math.floor(Math.random() * mustContain.length);
    password += mustContain[index];
    mustContain.splice(index, 1);
  }

  password = firstChar + password;

  if (password.length === 0) {
    password = "—";
  }

  // Return password
  return password;
}

// Pick a ramdom character from a string
function getRandomChar(str) {
  return str.charAt(Math.floor(Math.random() * str.length));
}

// Get amount of toggled permutations
export function getActiveSettingsAmount(settings) {
  let activeOptions = 0;
  [
    "enableUppercaseCharacters",
    "enableLowercaseCharacters",
    "enableNumberCharacters",
    "enableSpecialCharacters",
  ].forEach(function (item) {
    if (settings[item]) {
      activeOptions++;
    }
  });

  return activeOptions;
}

// Get the characters usable for the password
function getCharacterMap(settings) {
  let characters = "";

  if (settings.enableUppercaseCharacters) {
    characters += settings.uppercaseCharacters;
  }

  if (settings.enableLowercaseCharacters) {
    characters += settings.lowercaseCharacters;
  }

  if (settings.enableNumberCharacters) {
    characters += settings.numberCharacters;
  }

  if (settings.enableSpecialCharacters) {
    characters += settings.specialCharacters;
  }

  return characters;
}

// Get strinified number from settings
export function getPossibilitiesEstimate(permutations, settings) {
  return stringifyNumber(permutations, settings.showFullPermutationNumber);
}

// Count permutations
export function countPermutations(settings) {
  let characters = getCharacterMap(settings);
  if (settings.avoidSimilarCharacters) {
    characters = characters
      .split("")
      .filter((char) => !settings.similarCharacters.includes(char))
      .join("");
  }
  const charactersAmount = characters.length;
  let passwordLength = settings.passwordLength;
  let permutations = charactersAmount;

  while (passwordLength > 1) {
    permutations = permutations * charactersAmount;
    passwordLength--;
  }
  return permutations;
}

function localDecimalSeparator() {
  var n = 1.1;
  n = n.toLocaleString().substring(1, 2);
  return n;
}

// Show a large number in text
function stringifyNumber(number, showFullPermutationNumber) {
  // Validate input
  if (!number) {
    return "";
  }

  // Return rounded number when not at least a billion
  if (number < 1000 * 1000 * 1000) {
    return number.toLocaleString();
  }

  // Return full number
  if (showFullPermutationNumber) {
    const numberStr = number.toLocaleString("fullwide", {
      useGrouping: true,
    });
    return numberStr;
  }

  // Number names
  const numberNames = [
    "Thousand",
    "Million", // https://en.wikipedia.org/wiki/Names_of_large_numbers
    "Billion",
    "Trillion",
    "Quadrillion",
    "Quintillion",
    "Sextillion",
    "Septillion",
    "Octillion",
    "Nonillion",
    "Decillion",
    "Undecillion",
    "Duodecillion",
    "Tredecillion",
    "Quattuordecillion",
    "Quindecillion",
    "Sedecillion",
    "Septendecillion",
    "Octodecillion",
    "Novemdecillion",
    "Vigintillion",
    "Unvigintillion",
    "Duovigintillion",
    "Tresvigintillion",
    "Quattuorvigintillion",
    "Quinvigintillion",
    "Sesvigintillion",
    "Septemvigintillion",
    "Octovigintillion",
    "Novemvigintillion",
    "Trigintillion",
    "Untrigintillion",
    "Duotrigintillion",
    "Trestrigintillion",
    "Quattuortrigintillion",
    "Quintrigintillion",
    "Sestrigintillion",
    "Septentrigintillion",
    "Octotrigintillion",
    "Noventrigintillion",
    "Quadragintillion",
    "Unquadragintillion", // https://en.wiktionary.org/wiki/Appendix:Very_large_numbers
    "Duoquadragintillion",
    "Trequadragintillion",
    "Quattuorquadragintillion",
  ];

  // Get the number as a string
  let numberStr = number.toLocaleString("fullwide", {
    useGrouping: false,
  });
  const numberStrOrg = numberStr;

  // Start a index null
  let index = 0;

  // Keep looping when string is at least 4 chars
  while (numberStr.length >= 4) {
    numberStr = numberStr.substring(0, numberStr.length - 3);
    index++;
  }

  // Add decimals
  const decimal = numberStrOrg.substring(
    numberStr.length,
    numberStr.length + 1
  );

  if (numberStr.length <= 2 && decimal !== "0") {
    numberStr +=
      localDecimalSeparator() +
      numberStrOrg.substring(numberStr.length, numberStr.length + 1);
  }

  // Substract 2 from index to fix wrong numbers
  index = index - 1;

  return numberStr + " " + numberNames[index];
}

// Copy to clipboard
export function copyToClipboard(text) {
  var input = document.createElement("input");
  input.setAttribute("value", text);
  document.body.appendChild(input);
  input.select();
  input.setSelectionRange(0, 1000);
  var result = document.execCommand("copy");
  document.body.removeChild(input);
  return result;
}

// Estimate time to brute-force
export function getEstimatedBruteForceTime(permutations) {
  const MS = 1;
  const SEC = 1000 * MS;
  const MIN = 60 * SEC;
  const HOUR = 60 * MIN;
  const DAY = 24 * HOUR;
  const YEAR = 365.25 * DAY;
  const AGE_EARTH_YEARS = 4.54e9;
  const AGE_UNIVERSE_YEARS = 13.8e9;

  const UNIVERSE_TIERS = [
    { limit: 1, label: "times the age of the universe" },
    { limit: 1e3, label: "thousand times the age of the universe" },
    { limit: 1e6, label: "million times the age of the universe" },
    { limit: 1e9, label: "billion times the age of the universe" },
    { limit: 1e12, label: "trillion times the age of the universe" },
    { limit: 1e15, label: "quadrillion times the age of the universe" },
  ];

  const ATTEMPTS_PER_SECOND = 1e9; // 1 billion guesses/sec

  const seconds = permutations / ATTEMPTS_PER_SECOND;
  const milliseconds = seconds * 1000;
  const years = seconds / (YEAR / 1000);

  if (milliseconds < 1) {
    return `less than 1 millisecond`;
  }

  if (milliseconds < SEC) {
    return `~${Math.ceil(Math.max(2, milliseconds))} milliseconds`;
  }

  if (milliseconds < MIN) {
    return `~${Math.ceil(Math.max(2, milliseconds / SEC))} seconds`;
  }

  if (milliseconds < HOUR) {
    return `~${Math.ceil(Math.max(2, milliseconds / MIN))} minutes`;
  }

  if (milliseconds < DAY) {
    return `~${Math.ceil(Math.max(2, milliseconds / HOUR))} hours`;
  }

  if (milliseconds < YEAR) {
    return `~${Math.ceil(Math.max(2, milliseconds / DAY))} days`;
  }

  if (years < 100) {
    return `~${Math.ceil(Math.max(2, years))} years`;
  }

  if (years < 1_000_000) {
    return `~${(years / 1000).toFixed(0)} thousand years`;
  }

  if (years < AGE_EARTH_YEARS) {
    return `~${(years / 1e6).toFixed(0)} million years`;
  }

  if (years < AGE_UNIVERSE_YEARS) {
    return `~${(years / AGE_EARTH_YEARS).toFixed(
      0
    )} times the age of the Earth`;
  }

  const universeMultiple = years / AGE_UNIVERSE_YEARS;

  for (const tier of UNIVERSE_TIERS) {
    if (universeMultiple < tier.limit * 1000) {
      const value = universeMultiple / tier.limit;
      return `~${value.toFixed(0)} ${tier.label}`;
    }
  }

  return `So long that physics will probably break first`;
}
